//** Common functions **/
// Creates a time series of doses from user input
import { Dose } from '@/models/Dose';
import { Alerts, DoseTypes } from '@/models/utils/constants';
import {
  samplePatient,
  samplePatientThom2009,
  samplePatient2Compartment,
  samplePatientGentamicin,
  samplePatientLlop2006,
  samplePatientAdan2015,
  samplePatientMasi2020,
  samplePatientGoti2018,
  samplePatientGoti2018Dialysis,
} from '@/models/utils/defaults';
import moment from 'moment/moment';
import { DynamicSimulation } from '@/models/DynamicSimulation';
import { Drug } from '@/models/Drug';
import { DrugModel } from '@/models/DrugModel';
import { Delivery } from '@/models/Delivery';
import { Target } from '@/models/Target';
import { Pathogen } from '@/models/Pathogen';

export const DATEFORMAT = {
  DMY: 'DD/MM/YYYY HH:mm',
  YMD: 'YYYY-MM-DD HH:mm',
  YMDA: 'YYYY-MM-DD hh:mm A',
  UTC: 'YYYY-MM-DDTHH:mm:ssZ',
  NA: 'Not applicable',
};

// Format of Time in CSVs exported by this calculator. Kept separate from DATEFORMAT.YMD
// on purpose: this is the file contract for our own exports, YMD is a user option.
export const EXPORT_DATEFORMAT = 'YYYY-MM-DD HH:mm';
// Formats accepted when re-importing our own exports (current, then pre-2026-10 AM/PM)
const DOSING_IMPORT_FORMATS = [EXPORT_DATEFORMAT, 'YYYY-MM-DD hh:mm A'];

export const FILESOURCE = {
  DOSING: 'Heidi Dosing Calculator',
  IDODS: 'ID-ODS shiny app',
  PMETRICS: 'PMetrics Data - single patient',
};

export const ERROR = {
  INVALID: 'Data is invalid',
};

export const createDoseTimeSeries = (initItem, item, number, incrementValue, incrementType) => {
  let list = initItem.amount ? [initItem] : [];
  let startDate = initItem.datetime ? new Date(initItem.datetime) : new Date();

  for (let i = 0; i < number; i++) {
    if (list.length > 0) {
      if (incrementType === 'hours') {
        startDate.setHours(startDate.getHours() + parseInt(incrementValue));
      } else if (incrementType === 'days') {
        startDate.setDate(startDate.getDate() + parseInt(incrementValue));
      }
    }
    list.push({ ...item, id: item.id + i, datetime: new Date(startDate) });
  }
  return list;
};

// Expects whole hours
export const getRelativeDate = (hours, startDate, back = true) => {
  const days = Math.floor(hours / 24);
  const hrs = Math.floor(hours % 24);
  const previous = startDate ? new Date(startDate) : new Date();
  if (days > 0) {
    if (back === true) {
      previous.setDate(previous.getDate() - days);
    } else {
      previous.setDate(previous.getDate() + days);
    }
  }
  if (hrs > 0) {
    if (back === true) {
      previous.setHours(previous.getHours() - hrs);
    } else {
      previous.setHours(previous.getHours() + hrs);
    }
  }
  return previous;
};
// A time with AM/PM must be read with a 12-hour format, or PM times shift by 12 hours
const withMeridiem = (time, format) =>
  /\b[ap]\.?m\.?$/i.test(time) && !format.includes('A')
    ? `${format.replace('HH', 'hh')} A`
    : format;

// Import date and time from CSV data
// @param importedTime: date and time as one string
// @param formatType: datetime format
export const convertDateTime = (importedTime, formatType) => {
  const time = String(importedTime ?? '').trim();
  const d = moment(time, withMeridiem(time, formatType)).toDate();
  if (d instanceof Date && !isNaN(d)) {
    return d;
  } else {
    return ERROR.INVALID;
  }
};

// Strict parse with the picked format, also allowing 1-digit day/month/hour and seconds,
// as spreadsheet apps write them (e.g. an export re-saved in Excel)
const parseResavedTime = (time, format) => {
  let formats = [withMeridiem(time, format)];
  for (const [two, one] of [
    ['DD', 'D'],
    ['MM', 'M'],
    [/HH|hh/, (h) => h[0]],
  ]) {
    formats = formats.flatMap((f) => [f, f.replace(two, one)]);
  }
  formats = formats.flatMap((f) => [f, f.replace('mm', 'mm:ss')]);
  const m = moment(time, [...new Set(formats)], true);
  return m.isValid() ? m.toDate() : ERROR.INVALID;
};

export const importDoseFromRow = (sourceFormat, dateFormat, row, start) => {
  let dose = null;
  if (sourceFormat === 'IDODS') {
    const type =
      row.Type.indexOf('Dosing') === 0
        ? DoseTypes.DOSE
        : row.Type === 'Concentration'
          ? DoseTypes.CONC
          : row.Type === 'Creatinine'
            ? DoseTypes.SECR
            : null;

    if (type) {
      const datetime = convertDateTime(row.Time, DATEFORMAT[dateFormat]);
      if (datetime !== ERROR.INVALID) {
        if (type === DoseTypes.DOSE) {
          dose = new Dose({
            type,
            datetime,
            amount: parseFloat(row.Value),
            amountUnit: 'mg',
            duration: parseFloat(row.Infusion),
            durationUnit: 'hours',
          });
        } else if (type === DoseTypes.CONC) {
          dose = new Dose({
            type,
            datetime,
            amount: parseFloat(row.Value),
            amountUnit: 'mg/L',
            _rowVariant: 'info',
          });
        } else if (type === DoseTypes.SECR) {
          dose = new Dose({
            type,
            datetime,
            amount: parseFloat(row.Value),
            amountUnit: 'umol/L',
            _rowVariant: 'success',
          });
        }
      } else {
        dose = ERROR.INVALID;
      }
    }
  } else if (sourceFormat === 'DOSING') {
    // Our own export formats first, then the picked format (e.g. an export re-saved in Excel)
    const time = String(row.Time ?? '').trim();
    const m = moment(time, DOSING_IMPORT_FORMATS, true);
    const datetime = m.isValid() ? m.toDate() : parseResavedTime(time, DATEFORMAT[dateFormat]);
    if (datetime !== ERROR.INVALID) {
      if (row.Type === DoseTypes.DOSE) {
        dose = new Dose({
          type: row.Type,
          datetime,
          amount: parseFloat(row.Value),
          amountUnit: row.Value_unit,
          duration: parseFloat(row.Infusion),
          durationUnit: row.Infusion_unit,
        });
      } else if (row.Type === DoseTypes.CONC) {
        dose = new Dose({
          type: row.Type,
          datetime,
          amount: parseFloat(row.Value),
          amountUnit: row.Value_unit,
          _rowVariant: 'info',
        });
      } else if (row.Type === DoseTypes.SECR) {
        dose = new Dose({
          type: row.Type,
          datetime,
          amount: parseFloat(row.Value),
          amountUnit: row.Value_unit,
          _rowVariant: 'success',
        });
      }
    } else {
      dose = ERROR.INVALID;
    }
  } else if (sourceFormat === 'PMETRICS') {
    const datetime = moment(start).add(parseFloat(row.TIME), 'hours').toDate();
    if (parseInt(row.EVID) === 1) {
      dose = new Dose({
        type: DoseTypes.DOSE,
        datetime,
        amount: parseFloat(row.DOSE),
        amountUnit: 'mg',
        duration: parseFloat(row.DUR),
        durationUnit: 'hours',
      });
    } else if (parseInt(row.EVID) === 0) {
      dose = new Dose({
        type: DoseTypes.CONC,
        datetime,
        amount: parseFloat(row.OUT),
        amountUnit: 'mg/L',
        _rowVariant: 'info',
      });
    }
  }
  return dose;
};
// Doses are duplicates when they have the same type and time to the minute
const doseKey = (d) => `${d.type}|${moment(d.datetime).startOf('minute').valueOf()}`;

// Doses from `incoming` that duplicate neither `existing` nor an earlier incoming dose
export const newDosesOnly = (existing, incoming) => {
  const seen = new Set(existing.map(doseKey));
  return incoming.filter((d) => {
    const key = doseKey(d);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Remove duplicate doses, keeping the first occurrence
export const deduplicateDoses = (doses) => newDosesOnly([], doses);

export const DUPLICATE_MESSAGE = 'This is a duplicate by type and datetime. Please try again.';

// Message for the result of store.addDose: an error if nothing was added, a warning with the
// skipped count if some were duplicates, otherwise the caller's success text
export const addDoseFeedback = ({ added, skipped }, success) => {
  if (added === 0)
    return { type: 'error', text: skipped > 0 ? DUPLICATE_MESSAGE : 'No doses to add' };
  if (skipped > 0) {
    return {
      type: 'warning',
      text: `${success} (${skipped} duplicate${skipped === 1 ? '' : 's'} skipped)`,
    };
  }
  return { type: 'success', text: success };
};

export const calculateSince = (doses) => {
  const sortedDoses = doses.sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
  let first = sortedDoses.filter((d) => d.type === DoseTypes.DOSE)[0] ?? null;
  /** validation limits **/
  const maxInterval = 24 * 7;
  const maxWeekHrs = 24 * 7;
  if (first) {
    const duration = first.durationUnit === 'hours' ? first.duration * 1 : first.duration / 60;
    doses = sortedDoses.map((e, _index, _ary) => {
      // By identity: ids can repeat (series ids, saved patients vs the Dose counter)
      if (e !== first) {
        const dt = (e.datetime.getTime() - first.datetime.getTime()) / (1000 * 60 * 60);
        e.since = dt.toFixed(2); // hrs
        e.sinceAlert = null;
        if (e.type === DoseTypes.DOSE) {
          e.sinceAlert =
            e.since < 1 + duration
              ? Alerts.SINCE_INFUSION
              : e.since > maxInterval
                ? Alerts.SINCE_MAX_WEEK
                : null;
          first = e;
        } else if (e.type === DoseTypes.SECR) {
          e.sinceAlert =
            e.since < -maxWeekHrs
              ? Alerts.SINCE_MAX_WEEK
              : e.since > maxWeekHrs
                ? Alerts.SINCE_MAX_WEEK
                : null;
        } else if (e.type === DoseTypes.CONC) {
          e.sinceAlert =
            e.since < 0
              ? Alerts.SINCE_BEFORE
              : e.since < 1 + duration
                ? Alerts.SINCE_INFUSION
                : e.since > maxWeekHrs
                  ? Alerts.SINCE_MAX_WEEK
                  : null;
        }
      } else {
        e.since = null;
        e.sinceAlert = null;
      }
      return e;
    });
  }
  return doses;
};

// Create a patient for testing using current date or provided date
// Using Vancomycin estimates
export const createSamplePatient = (startDate) => {
  // set samplePatient doses to current date
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 88,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 2500,
      amountUnit: 'mg',
      duration: 1.5,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(12, testStartDate, false),
      amount: 1250,
      amountUnit: 'mg',
      duration: 1.5,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 24,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(10, testStartDate, false),
      amount: 16,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatient, doses: [...updatedDoses] };
};

// Using Vancomycin THOM2009 estimates (same patient as BUEL2005 sample, 1h infusion)
export const createSamplePatientThom2009 = (startDate) => {
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 88,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 2500,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(12, testStartDate, false),
      amount: 1250,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 24,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(10, testStartDate, false),
      amount: 16,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatientThom2009, doses: [...updatedDoses] };
};

// Sample using Piperacillin estimates
export const createSamplePatient2Compartment = (startDate) => {
  // set samplePatient doses to current date
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.CLCR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 80,
      amountUnit: 'mL/min',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: testStartDate,
      amount: 4500,
      amountUnit: 'mg',
      duration: 20,
      durationUnit: 'minutes',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 24,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(10, testStartDate, false),
      amount: 16,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatient2Compartment, doses: [...updatedDoses] };
};

// Sample using Vancomycin LLOP2006 (2-compartment ICU model, Llopis-Salvia 2006)
export const createSamplePatientLlop2006 = (startDate) => {
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 88,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 1500,
      amountUnit: 'mg',
      duration: 1.5,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(12, testStartDate, false),
      amount: 1500,
      amountUnit: 'mg',
      duration: 1.5,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 22,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(10, testStartDate, false),
      amount: 12,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatientLlop2006, doses: [...updatedDoses] };
};

// Sample using Adane 2015 extremely obese vancomycin model
export const createSamplePatientAdan2015 = (startDate) => {
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 72,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 2000,
      amountUnit: 'mg',
      duration: 1.5,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(12, testStartDate, false),
      amount: 2000,
      amountUnit: 'mg',
      duration: 1.5,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 18,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(10, testStartDate, false),
      amount: 8,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatientAdan2015, doses: [...updatedDoses] };
};

// Sample using Masich 2020 obese sepsis vancomycin model
export const createSamplePatientMasi2020 = (startDate) => {
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 115,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 1500,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(12, testStartDate, false),
      amount: 1500,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 20,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(10, testStartDate, false),
      amount: 11,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatientMasi2020, doses: [...updatedDoses] };
};

// Sample using Goti 2018 hospitalized vancomycin model (non-dialysis)
export const createSamplePatientGoti2018 = (startDate) => {
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 80,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 1500,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(12, testStartDate, false),
      amount: 1500,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 20,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(10, testStartDate, false),
      amount: 12,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatientGoti2018, doses: [...updatedDoses] };
};

// Sample using Goti 2018 hospitalized vancomycin model (hemodialysis)
export const createSamplePatientGoti2018Dialysis = (startDate) => {
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 265,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 1000,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(48, testStartDate, false),
      amount: 1000,
      amountUnit: 'mg',
      duration: 1,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(4, testStartDate, false),
      amount: 18,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(36, testStartDate, false),
      amount: 8,
      amountUnit: 'mcg/mL',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
  ];
  return { ...samplePatientGoti2018Dialysis, doses: [...updatedDoses] };
};

// Sample using Gentamicin ICU model (Gomes 2017)
export const createSamplePatientGentamicin = (startDate) => {
  const testStartDate = startDate ?? getRelativeDate(24, null);
  const updatedDoses = [
    new Dose({
      type: DoseTypes.SECR,
      datetime: getRelativeDate(26, testStartDate, true),
      amount: 88,
      amountUnit: 'umol/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'success',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: new Date(testStartDate),
      amount: 360,
      amountUnit: 'mg',
      duration: 0.5,
      durationUnit: 'hours',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(2, testStartDate, false),
      amount: 13,
      amountUnit: 'mg/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.CONC,
      datetime: getRelativeDate(20, testStartDate, false),
      amount: 0.8,
      amountUnit: 'mg/L',
      duration: null,
      durationUnit: null,
      _rowVariant: 'info',
    }),
    new Dose({
      type: DoseTypes.DOSE,
      datetime: getRelativeDate(24, testStartDate, false),
      amount: 360,
      amountUnit: 'mg',
      duration: 0.5,
      durationUnit: 'hours',
    }),
  ];
  return { ...samplePatientGentamicin, doses: [...updatedDoses] };
};

export const createTestData = (drug, drugModel, startDate, delivery, target, pathology) => {
  const testData = createSamplePatient(startDate);
  return new DynamicSimulation({
    patient: testData.patient,
    drug: drug instanceof Drug ? drug : new Drug(drug),
    model: drugModel instanceof DrugModel ? drugModel : new DrugModel(drugModel),
    doses: testData.doses,
    delivery:
      delivery instanceof Delivery ? delivery : new Delivery({ daysToDisplay: 3, ...delivery }),
    target: target instanceof Target ? target : new Target(target),
    pathology: pathology ? new Pathogen(pathology) : null,
  });
};
